<script lang="ts" setup>
import SortItems from '@/components/common/SortItems.vue';
import { Category } from '@/models/category.model';
import { ShiftCMSState } from '@/store';
import { CategoriesActions, CategoriesState } from '@/stores/categories.store';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';

const store = useStore<ShiftCMSState & { cs: CategoriesState }>();
const categories = computed<Category[]>(() => store.state.cs.categories);

const drag = ref<boolean>(false);
const dragItem = ref<Category | null>(null);

const dragStart = (e: Event, el: Category) => {
  e.stopPropagation();
  drag.value = true;
  dragItem.value = el;

  return null;
};

const dragEnd = () => {
  drag.value = false;
  return null;
};

const dragDrop = async (evt: DragEvent, el: Category | null) => {
  evt.preventDefault();
  drag.value = false;

  await store.dispatch(CategoriesActions.UpdateCategory, {
    ...dragItem.value,
    parentCategory: el,
  });

  await store.dispatch(CategoriesActions.GetCategories);

  dragItem.value = null;
};

const dragOver = (evt: DragEvent) => {
  evt.preventDefault();

  if (evt.target) {
    (evt.target as Element).classList.add('drag-active');
  }
};
const dragLeave = (evt: DragEvent) => {
  evt.preventDefault();
  if (evt.target) {
    (evt.target as Element).classList.remove('drag-active');
  }
};

const updateSorting = async (ids: number[]) => {
  await store.dispatch(CategoriesActions.UpdateCategoriesOrder, ids);
};
</script>
<template>
  <v-container>
    <h2>Edit category hierarchy</h2>
    <v-alert type="info">
      Drag the category onto their parent. This is allowed only for
      <i>two levels</i>.
    </v-alert>

    <v-card class="mt-4 pb-4">
      <ul class="cat-list mt-8 ml-8 draglist">
        <li
          v-for="cat in categories
            .filter((c) => !c.parentCategory)
            .sort((a, b) => a.index - b.index)"
          :key="cat.id"
        >
          <div class="item">
            <div
              class="name"
              draggable="true"
              @dragstart="(e) => dragStart(e, cat)"
              @dragend="dragEnd"
            >
              {{ cat.title }}
            </div>
            <div
              v-if="drag && dragItem?.id !== cat.id"
              class="drop-target"
              @drop="(e) => dragDrop(e, cat)"
              @dragover="(e) => dragOver(e)"
              @dragleave="(e) => dragLeave(e)"
            ></div>
          </div>
          <ul class="cat-list">
            <li
              v-for="cat2 in categories
                .filter((c) => c.parentCategory?.id === cat.id)
                .sort((a, b) => a.index - b.index)"
              :key="cat2.id"
            >
              <div class="item">
                <div
                  class="name"
                  draggable="true"
                  @dragstart="(e) => dragStart(e, cat2)"
                  @dragend="dragEnd"
                >
                  {{ cat2.title }}
                </div>
                <div
                  v-if="drag && dragItem?.id !== cat2.id"
                  class="drop-target"
                  @drop="(e) => dragDrop(e, cat2)"
                  @dragover="(e) => dragOver(e)"
                  @dragleave="(e) => dragLeave(e)"
                ></div>
              </div>
              <ul class="cat-list">
                <li
                  v-for="cat3 in categories
                    .filter((c) => c.parentCategory?.id === cat2.id)
                    .sort((a, b) => a.index - b.index)"
                  :key="cat3.id"
                >
                  <div class="item">
                    <div
                      class="name"
                      draggable="true"
                      @dragstart="(e) => dragStart(e, cat3)"
                      @dragend="dragEnd"
                    >
                      {{ cat3.title }}
                    </div>
                  </div>
                </li>
              </ul>
            </li>
          </ul>
        </li>
        <li v-if="drag">
          <div
            class="drop-target"
            @drop="(e) => dragDrop(e, null)"
            @dragover="(e) => dragOver(e)"
            @dragleave="(e) => dragLeave(e)"
          ></div>
        </li>
      </ul>
    </v-card>
    <h2 class="mt-8">Sort categories</h2>
    <SortItems
      :items="categories.sort((a, b) => a.index - b.index)"
      @change="updateSorting"
    />

    <v-row>
      <v-col col="2"></v-col>
      <v-col> </v-col>
      <v-col>
        <v-btn class="mt-2" type="reset" block>Close</v-btn>
      </v-col>
      <v-col col="2"></v-col>
    </v-row>
  </v-container>
</template>
<style scoped>
.cat-list {
  list-style-type: square;
  padding-left: 1.5em;
}

.draglist {
  list-style-type: square;
}

.item {
  display: flex;
  flex-direction: row;
}
.name {
  width: 200px;
  cursor: grab;
}
.drop-target {
  width: 200px;
  height: 24px;
  border: dashed 1px black;
  border-radius: 4px;
  border-color: rgb(0, 0, 0);
}
.drop-target.drag-active {
  border-color: rgba(46, 126, 246, 1);
  background-color: rgba(46, 126, 246, 0.5);
}
</style>
